import { useEffect, useState } from 'react';
import { Tabs } from '@zoopla-eng/zds-components/tabs';
import { SearchMedium } from '@zoopla-eng/zds/icons';
import {
    LocationSearchInput,
    QueryType
} from '@zoopla/listing-search-location-search-input';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { Box } from '@zoopla-eng/zds-components/box';
import { ContentfulImageWrapper } from '../../queries/heroContent.types';
import { content } from './constants';
import BackgroundImage from './HomepageHero.png';
import { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/router';
import triggerTracking from '@blueprint/tracking';
import {
    LocationSearchInputWrapper,
    Headline,
    SubHeader,
    TabWrapper,
    HeroSpacer,
    HeroContainer,
    HeroWrapper,
    heroImageStyles,
    StyledBold
} from './TabbedSearch.styles';
import { Button } from '@zoopla-eng/zds-components/button';
import { ButtonLeft } from '@zoopla-eng/zds/button';
import { Grid } from '@zoopla-eng/zds-components/grid';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';
import { css } from '@zoopla-eng/zds-components/themes';

const {
    missingSearchAreaError,
    searchAreaPlaceholder,
    saleQueryParams,
    rentQueryParams,
    housePricesQueryParams
} = content;

type TabContentProps = {
    searchForm: string;
};

const buildSearchUrl = (queryParams: ParsedUrlQuery) => {
    const searchPath = '/search/';
    const queryParamString = `?${Object.entries(queryParams)
        .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
        .join('&')}`;
    const url = `${searchPath}${queryParamString}`;
    return url;
};

const TabContent = ({ searchForm }: TabContentProps) => {
    const router = useRouter();
    const [submitted, setSubmitted] = useState(false);
    const [selectedSearchArea, setSelectedSearchArea] = useState<{
        id: string;
        text: string;
    } | null>({
        id: '',
        text: ''
    });
    const [searchAreaInputValue, setSearchAreaInputValue] = useState<
        string | undefined
    >();

    const searchValue = selectedSearchArea?.text || searchAreaInputValue || '';

    const isForSale = searchForm === 'for-sale';

    const handleSubmit = (event: React.SyntheticEvent) => {
        let queryParams;

        event.preventDefault();
        setSubmitted(true);

        if (!searchValue) {
            return;
        }

        if (isForSale) {
            queryParams = saleQueryParams;
        } else if (searchForm === 'to-rent') {
            queryParams = rentQueryParams;
        } else {
            queryParams = housePricesQueryParams;
        }

        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'search',
            eventLabel: `${searchForm}-success`
        });

        router.push(
            buildSearchUrl({
                ...queryParams,
                q: searchValue,
                geo_autocomplete_identifier: selectedSearchArea?.id ?? '',
                search_source: 'home'
            })
        );
    };

    return (
        <form onSubmit={handleSubmit} role="search">
            <Stack
                rowGap="0"
                {...{
                    '@M100': {
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'nowrap',
                        columnGap: '0px'
                    }
                }}
            >
                <Box
                    pl="$S300"
                    pr="$S300"
                    pt="$S200"
                    flexBasis="100%"
                    bpM100={{
                        flexBasis: '87%',
                        pt: '0'
                    }}
                >
                    <LocationSearchInputWrapper data-testid="autosuggest-search-input">
                        <LocationSearchInput
                            onSelectedItemChange={setSelectedSearchArea}
                            onInputValueChange={setSearchAreaInputValue}
                            label={'Enter a city, town or postcode'}
                            placeholder={searchAreaPlaceholder}
                            errorText={
                                submitted && !searchValue
                                    ? missingSearchAreaError
                                    : undefined
                            }
                            queryType={
                                searchForm === 'house-prices'
                                    ? QueryType.AddressPostcodes
                                    : QueryType.GeoLocations
                            }
                        />
                    </LocationSearchInputWrapper>
                </Box>
                <Box
                    flexBasis="100%"
                    p="0 $S300"
                    flexDirection="column"
                    display="flex"
                    bpM100={{
                        flexBasis: '13%',
                        flexDirection: 'row',
                        pl: '0',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 0
                    }}
                    {...{
                        '@S100': {
                            pb: '$S100'
                        }
                    }}
                >
                    <ButtonLeft
                        category="primary"
                        Icon={SearchMedium}
                        data-testid="search-btn"
                    >
                        Search
                    </ButtonLeft>
                </Box>
            </Stack>
        </form>
    );
};

const HomeValueTabContent = () => {
    return (
        <Box
            fontGroup="ladle"
            px="$S400"
            pt="$S200"
            pb="$S100"
            bpM100={{
                py: '$S100'
            }}
        >
            <Grid
                gridRowGap="$S400"
                gridColumnGap="$S200"
                alignItems="center"
                bpM100={{
                    gridTemplateColumns: 'minmax(min-content, 65%) auto',
                    ...{
                        justifyContent: 'center'
                    }
                }}
            >
                <StyledBold>
                    First, let’s find your home to see how much it’s worth
                </StyledBold>
                <Button
                    fw
                    category="primary"
                    data-testid="my-home-cta"
                    {...{ as: 'a', href: '/my-home/?search_source=home' }}
                >
                    Find my home
                </Button>
            </Grid>
        </Box>
    );
};

type TabbedSearchProps = {
    image: ContentfulImageWrapper;
    brandHeadline: string;
    h1TitleTag: string;
    isLoggedIn?: boolean;
};

export const TabbedSearch = ({
    brandHeadline,
    image,
    h1TitleTag,
    isLoggedIn
}: TabbedSearchProps) => {
    const [value, updateValue] = useState('for-sale');
    const [homeValueFeature] = useOptimizelyFeatureFlag('_home-value-search');

    const homeValueTabIsEnabled = homeValueFeature === true;

    const isForSale = value === 'for-sale';

    useEffect(() => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'channel_tab',
            eventLabel: `${value}`
        });
    }, [value]);

    const backgroundImageStyles = css({
        backgroundImage: `url(${
            image?.url ? `${image.url}?w=550&fm=webp&q=75&` : BackgroundImage
        })`,
        '@S200': {
            backgroundImage: `url(${
                image?.url
                    ? `${image.url}?w=768&fm=webp&q=75&`
                    : BackgroundImage
            })`
        },
        '@M100': {
            backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%),url(${
                image?.url
                    ? `${image.url}?w=1350&fm=webp&q=75&`
                    : BackgroundImage
            })`
        }
    });

    return (
        <Box fw={isLoggedIn}>
            <HeroWrapper isLoggedIn={isLoggedIn}>
                {!isLoggedIn && (
                    <div
                        className={`${heroImageStyles()} ${backgroundImageStyles()}`}
                    />
                )}
                <HeroContainer isLoggedIn={isLoggedIn}>
                    {!isLoggedIn && (
                        <Headline data-testid="homepage-headline">
                            {brandHeadline
                                ? brandHeadline
                                : 'We know what a home is really worth'}
                        </Headline>
                    )}

                    <SubHeader
                        isLoggedIn={isLoggedIn}
                        data-testid="homepage-subheader"
                    >
                        {h1TitleTag
                            ? h1TitleTag
                            : 'Find homes to buy or rent and check house prices'}
                    </SubHeader>
                    <TabWrapper isLoggedIn={isLoggedIn}>
                        <Tabs
                            label="Houses"
                            value={value}
                            alignment="centered"
                            tabs={[
                                {
                                    value: 'for-sale',
                                    label: 'For sale',
                                    component: <TabContent searchForm={value} />
                                },
                                {
                                    value: 'to-rent',
                                    label: 'To rent',
                                    component: <TabContent searchForm={value} />
                                },
                                homeValueTabIsEnabled
                                    ? {
                                          value: 'home-value',
                                          label: 'Home value',
                                          component: <HomeValueTabContent />
                                      }
                                    : {
                                          value: 'house-prices',
                                          label: 'House prices',
                                          component: (
                                              <TabContent searchForm={value} />
                                          )
                                      }
                            ]}
                            onValueChange={updateValue}
                        />
                    </TabWrapper>
                </HeroContainer>
            </HeroWrapper>
            <HeroSpacer isLoggedIn={isLoggedIn} />
        </Box>
    );
};
